import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Typography, Space, Divider, message } from 'antd';
import { MobileOutlined, MailOutlined, SafetyOutlined, LeftCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import './H5LoginPage.css';
import http from '../../utils/request';
import logo from '../../assets/images/logo.png';
import LanguageSwitcher from '../InvalidAidPage/LanguageSwitcher';
import { useLanguage } from '../../context/LanguageContext';
import Captcha from '../Captcha/Captcha';

const { Title, Text } = Typography;

const H5LoginPage = () => {
  const { language } = useLanguage();
  const [user, setUser] = useState('');
  const [authMethods, setAuthMethods] = useState([]);
  const [step, setStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(120);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verificationCodeError, setVerificationCodeError] = useState('');
  const [contactInfo, setContactInfo] = useState({});
  const [samlResponse, setSamlResponse] = useState('');
  const [relayState, setRelayState] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [hasCredential, setHasCredential] = useState(false);
  const [savedFn, setSavedFn] = useState([]);
  const formRef = useRef(null);
  const [verifyAccountLoading, setVerifyAccountLoading] = useState(false);
  const [verifyPwdLoading, setVerifyPwdLoading] = useState(false);
  const [verifySendCodeLoading, setVerifySendCodeLoading] = useState(false);
  const [verifyMultiFactorLoading, setVerifyMultiFactorLoading] = useState(false);
  const [helpLink, setHelpLink] = useState('');
  const [contactError, setContactError] = useState(false);

  const content = {
    CN: {
      accountLogin: "账号登录",
      accountDescription: "请使用公司域账号登录，没有域账号的用户使用工号登录",
      accountPlaceholder: "请输入公司域账号/工号",
      nextStep: "下一步",
      or: "或",
      travixAccount: "Travix账号",
      welcome: "欢迎",
      passwordPlaceholder: "请输入您的账号密码",
      passwordError: "用户名或密码错误",
      networkError: "网络错误，请稍后重试",
      chooseVerification: "选择验证方式",
      chooseVerificationDescription: "请选择您的验证方式继续",
      smsVerification: "通过短信验证码登录（每日限30次）",
      emailVerification: "通过邮箱验证码登录",
      itokenVerification: "使用itoken登录",
      voiceVerification: "通过电话语音验证码登录",
      smsTitle: "短信验证",
      emailTitle: "邮箱验证",
      itokenTitle: "iToken验证",
      voiceTitle: "电话语音验证",
      verificationTitle: "验证",
      smsDescription: (mobile) => `系统已将验证码发送到手机${mobile},请输入6位数验证码继续`,
      emailDescription: (email) => `系统已将验证码发送到邮箱${email},请输入6位数验证码继续`,
      itokenDescription: "请输入您的iToken验证码",
      voiceDescription: (phone) => `系统将电话语音通知验证码到手机${phone}，请注意接听`,
      enterVerificationCode: "请输入验证码",
      countdownInvalid: "秒后失效",
      codeInvalid: "验证码已失效",
      expired: "已失效,请点击",
      resendCode: "重新发送",
      chooseOtherVerification: "选择其他验证方式",
      accountNotExist: "账号不存在",
      incorrectVerificationCode: "请输入正确的验证码",
      noMobileFound: "未获取到手机号",
      noEmailFound: "未获取到邮箱",
      contactHR: "可联系人事补全联系方式",
      accountLocked: "当前账号已锁定，请解锁后重试",
      loginFailureTroubleshoot: "登录失败自助排查",
      switchVerificationMethodTip: "如果单个渠道无法收到验证码，可以返回上一页切换二次验证方式",
      contactError: "账号不存在或输入有误，请返回上一步重新输入",
      backButton: "返回",
    },
    EN: {
      accountLogin: "Account Login",
      accountDescription: "Please log in with your company domain account. Users without a domain account should use their employee ID.",
      accountPlaceholder: "Enter your company domain account/employee ID",
      nextStep: "Next",
      or: "or",
      travixAccount: "Travix Account",
      welcome: "Welcome",
      passwordPlaceholder: "Please enter your account password",
      passwordError: "Incorrect username or password",
      networkError: "Network error, please try again later",
      chooseVerification: "Choose Verification Method",
      chooseVerificationDescription: "Please choose your verification method to continue",
      smsVerification: "Login via SMS verification code (limited to 30 times per day)",
      emailVerification: "Login via email verification code",
      itokenVerification: "Login using itoken",
      voiceVerification: "Login via phone voice verification code",
      smsTitle: "SMS Verification",
      emailTitle: "Email Verification",
      itokenTitle: "iToken Verification",
      voiceTitle: "Phone Voice Verification",
      verificationTitle: "Verification",
      smsDescription: (mobile) => `The verification code has been sent to your mobile ${mobile}. Please enter the 6-digit code to continue.`,
      emailDescription: (email) => `The verification code has been sent to your email ${email}. Please enter the 6-digit code to continue.`,
      itokenDescription: "Please enter your iToken verification code",
      voiceDescription: (phone) => `The system will notify you of the verification code via phone call to ${phone}. Please answer the call.`,
      enterVerificationCode: "Please enter the verification code",
      countdownInvalid: "seconds to expire",
      codeInvalid: "Verification code has expired",
      expired: "Expired, please click to",
      resendCode: "resend",
      chooseOtherVerification: "Choose another verification method",
      accountNotExist: "Account does not exist",
      incorrectVerificationCode: "Please enter the correct verification code",
      noMobileFound: "No mobile number found",
      noEmailFound: "No email address found",
      contactHR: "Please contact HR to add it",
      accountLocked: "This account is locked. Please unlock it and try again.",
      loginFailureTroubleshoot: "Login Failure Self-Troubleshooting",
      switchVerificationMethodTip: "If you cannot receive the verification code through a specific channel, you can return to the previous page to switch your 2FA method.",
      contactError: "The account does not exist or the input is incorrect. Please go back and try again.",
      backButton: "Back",
    },
  };

  const t = content[language];

  const handleNext = ({user}) => {
    // 清除之前的错误信息
    setErrorMessage('');
    
    // 校验账号
    http.post('/api/v2/step/verifyAccount', { 
      user
    }).then(res => {
      setVerifyAccountLoading(false);
      // 判断异常
      if(res?.data?.code !== 0) {
        setErrorMessage(res?.data?.message);
        return;
      }
      // 检查账号是否合法
      if (res?.data?.result?.legal === false) {
        setErrorMessage(res?.data?.result?.message || t.accountNotExist);
        return;
      }
      
      let hasCredential = res?.data?.result?.hasCredential;
      setHasCredential(hasCredential);
      // let alertVerify = res?.data?.result?.alertVerify;
      // if(alertVerify) {
      //   setShowCaptcha(true);
      //   return;
      // }
      // 如果支持密码登录且已设置密码，进入step2
      if (authMethods.includes(1) && hasCredential) {
        setStep(2);
      } else {
        // 否则进入step3选择验证方式
        setStep(3);
      }
    }).catch(err => {
      setVerifyAccountLoading(false);
      setErrorMessage(t.networkError);
    });
  };

  const handleTravixLogin = () => {
    window.location.href = '/doTravixLogin';
  };

  const handleBack = () => {
    if (step === 4) {
      setStep(3);
      setVerificationCode('');
      setVerificationCodeError('');
      setIsCountingDown(false);
      setCountdown(120);
      setErrorMessage('');
    } else if (step === 2) {
      setStep(1);
      setPassword('');
      setPasswordError('');
    } else {
      setStep(1);
      setContactError(false);
    }
  };

  const handleVerificationMethodSelect = ({type}) => {
    setSelectedMethod(type);
    // 发送验证码
    http.post('/api/v2/step/send_code', {
      type
    }).then(res => {
      setVerifySendCodeLoading(false);
      if(res?.data?.code !== 0) {
        message.error(res?.data?.message);
        return;
      }
      if(res?.data?.result?.code === -1) {
        message.error(res?.data?.result?.en || t.networkError);
        return;
      }
      if (res?.data?.result?.code === 0 || res?.data?.result?.countDown) {
        setStep(4);
        setErrorMessage('');
        // 只有短信、邮箱和语音验证才需要倒计时
        if (type === 'sms' || type === 'email' || type === 'phone') {
          if (res?.data?.result?.countDown) {
            setCountdown(res.data.result.countDown);
          }
          setIsCountingDown(true);
        }
      }
    }).catch(err => {
      setVerifySendCodeLoading(false);
      setErrorMessage(t.networkError);
    });
  };

  const handleResendCode = () => {
    if(verifySendCodeLoading) {
      return;
    }
    // 重新发送验证码
    http.post('/api/v2/step/send_code', {
      type: selectedMethod
    }).then(res => {
      setVerifySendCodeLoading(false);
      if (res?.data?.result?.code === 0 || res?.data?.result?.countDown) {
        setVerificationCode(''); // Clear old code
        if (res?.data?.result?.countDown) {
          setCountdown(res.data.result.countDown);
        } else {
          setCountdown(120);
        }
        setIsCountingDown(true);
      }
      if(res?.data?.result?.code === -1) {
        message.error(res?.data?.result?.en || t.networkError);
        return;
      }
      if(res?.data?.code !== 0) {
        message.error(res?.data?.message || t.networkError);
      }
    }).catch(err => {
      setVerifySendCodeLoading(false);
      setErrorMessage(t.networkError);
    });
  };

  const handleVerificationCodeChange = (value) => {
    setVerificationCode(value);
    if (verificationCodeError) {
      setVerificationCodeError('');
    }
  };

  const handleVerificationSubmit = () => {
    if (verificationCode.length === 6) {
      // 清除之前的错误信息
      setVerificationCodeError('');
      setVerifyMultiFactorLoading(true);
      http.post('/api/v2/step/verifyMultiFactor', {
        code: verificationCode,
        type: selectedMethod
      }).then(res => {
        setVerifyMultiFactorLoading(false);
        if (res?.data?.redirectUrl) {
          setSamlResponse(res.data.result);
          setRelayState(res.data.relayState);
          setRedirectUrl(res.data.redirectUrl);
        }else {
          setVerificationCodeError(t.incorrectVerificationCode);
        }
      }).catch(err => {
        setVerifyMultiFactorLoading(false);
        setVerificationCodeError(t.networkError);
      });
    }
  };

  const handlePasswordSubmit = ({password}) => {
    if (password) {
      // 清除之前的密码错误信息
      setPasswordError('');
      setHelpLink('');

      http.post('/api/v2/step/verifyPwd', {
        password: password
      }).then(res => {
        setVerifyPwdLoading(false);
        if (res?.data?.result === 'PASS') {
          // 密码验证成功，跳转到step3
          setStep(3);
        } else if (res?.data?.result === 'NOT_PASS') {
          // 密码验证失败
          setPasswordError(t.passwordError);
          // 保存helpLink
          if (res?.data?.helpLink) {
            setHelpLink(res.data.helpLink);
          }
        } else if(res?.data?.result === 'LOCKED') {
          setPasswordError(t.accountLocked);
        } else {
          setPasswordError(t.passwordError);
        }
      }).catch(err => {
        setVerifyPwdLoading(false);
        setPasswordError(t.networkError);
      });
    }
  };

  const handleTokenReceived = (captchaResponse) => {
    http.post('/api/v2/step/verifyToken', {...captchaResponse,step:savedFn[2]}).then(res => {
      if (res?.data?.result) {
        setShowCaptcha(false);
        savedFn[0](savedFn[1]);
        // if (authMethods.includes(1) && hasCredential) {
        //   setStep(2);
        // } else {
        //   // 否则进入step3选择验证方式
        //   setStep(3);
        // }
      } else {
        // 验证失败，可以考虑提示用户
        setShowCaptcha(false);
        message.error(res?.data?.message);
        if(savedFn[2] === 'VerifyAccount') {
          setVerifyAccountLoading(false);
        }
        if(savedFn[2] === 'VerifyPwd') {
          setVerifyPwdLoading(false);
        }
        if(savedFn[2] === 'SendVerifyCode') {
          setVerifySendCodeLoading(false);
        }
      }
    }).catch(err => {
      setShowCaptcha(false);
      // setErrorMessage(t.networkError);
      message.error(err?.message);
      if(savedFn[2] === 'VerifyAccount') {
        setVerifyAccountLoading(false);
      }
      if(savedFn[2] === 'VerifyPwd') {
        setVerifyPwdLoading(false);
      }
      if(savedFn[2] === 'SendVerifyCode') {
        setVerifySendCodeLoading(false);
      }
    });
  };
  // 包装预检请求
  const wrapPrecheck = (fn,step) => {
    return function(params) {
      if(step === 'VerifyAccount') {
        setVerifyAccountLoading(true);
      }
      if(step === 'VerifyPwd') {
        setVerifyPwdLoading(true);
      }
      if(step === 'SendVerifyCode') {
        if(verifySendCodeLoading) {
          return;
        }
        setVerifySendCodeLoading(true);
      }
      // 调用预检接口
      // 生成参数
      http.post('/api/v2/step/preFlight', params).then(res => {
        if(res?.data?.code !== 0) {
          message.error(res?.data?.message);
          if(step === 'VerifyAccount') {
            setVerifyAccountLoading(false);
          }
          if(step === 'VerifyPwd') {
            setVerifyPwdLoading(false);
          }
          if(step === 'SendVerifyCode') {
            setVerifySendCodeLoading(false);
          }
          return;
        }
        if(res?.data?.result?.alertVerify) {
          // 如果需要出验证码，展示验证码，缓存函数和参数，方便验证码回调时候调用
          setShowCaptcha(true);
          setSavedFn([fn,params,step]);
        }else {
          //  如果不需要出验证码，直接执行函数
          fn(params);
        }
      }).catch(err => {
        if(step === 'VerifyAccount') {
          setVerifyAccountLoading(false);
        }
        if(step === 'VerifyPwd') {
          setVerifyPwdLoading(false);
        }
        if(step === 'SendVerifyCode') {
          setVerifySendCodeLoading(false);
        }
        message.error(err?.message);
      });
    }
  }
  // 账号校验
  const precheckHandleNext = wrapPrecheck(handleNext, 'VerifyAccount');
  // 密码校验
  const precheckHandlePassword = wrapPrecheck(handlePasswordSubmit, 'VerifyPwd');
  // 发送验证码校验
  const precheckHandleSendCode = wrapPrecheck(handleVerificationMethodSelect, 'SendVerifyCode');
  // 重新发送验证码
  const precheckHandleResendCode = wrapPrecheck(handleResendCode, 'SendVerifyCode');

  useEffect(() => {
    if (step === 3 && (authMethods.includes(4) || authMethods.includes(5) || authMethods.includes(8))) {
      setContactError(false);
      http.post('/api/v2/step/contact').then(res => {
        if (res?.data?.code !== 0) {
          setContactError(true);
          return;
        }
        if (res?.data?.result) {
          setContactInfo(res.data.result);
        }
      }).catch(() => {
        setContactError(true);
      });
    }
  }, [step, authMethods]);

  useEffect(() => {
    if (redirectUrl && formRef.current) {
      formRef.current.submit();
    }
  }, [redirectUrl]);

  const getLoginMethod = () => {
    // 发起请求，地址 /sso/spInfo
    http.get('/api/base/sso/spInfo').then(res => {
      // authMethods:[1,2,3,4,5,6,7,8] // 1-密码 2-扫码 3-Trippal 4-Email 5-SMS 6-IToken 7-travix 8-语音验证码
      if(res?.data?.result?.authMethods?.length) {
        setAuthMethods(res?.data?.result?.authMethods);
      }
    })
  }

  useEffect(() => {
    getLoginMethod();
  }, []);

  useEffect(() => {
    let timer;
    if (isCountingDown && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsCountingDown(false);
    }
    return () => clearTimeout(timer);
  }, [isCountingDown, countdown]);

  const renderStep1 = () => (
    <>
      <Title level={2} className="h5-form-title">{t.accountLogin}</Title>
      <Text className="h5-form-description">
        {t.accountDescription}
      </Text>
      
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          placeholder={t.accountPlaceholder}
          value={user}
          onChange={(e) => {
            setUser(e.target.value);
            // 用户输入时清除错误信息
            if (errorMessage) {
              setErrorMessage('');
            }
          }}
          size="large"
          status={errorMessage ? 'error' : ''}
          style={{ 
            borderRadius: '8px',
            height: '44px',
            fontSize: '14px'
          }}
        />
        
        {errorMessage && (
          <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
            {errorMessage}
          </div>
        )}
        
        <Button
          size="large"
          onClick={() => precheckHandleNext({user})}
          disabled={!user}
          loading={verifyAccountLoading}
          type="primary"
          block
          style={{
            height: '44px',
            fontSize: '14px',
            borderRadius: '8px'
          }}
        >
          {t.nextStep}
        </Button>
      </Space>
      
      {authMethods.includes(7) && (
        <>
          <div className="h5-form-divider">
            <Divider>{t.or}</Divider>
          </div>
          
          <div className="h5-travix-login">
            <a onClick={handleTravixLogin} className="h5-travix-link">
              {t.travixAccount}
            </a>
          </div>
        </>
      )}
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="h5-step-header" onClick={handleBack} style={{ cursor: 'pointer'}}>
        <LeftCircleOutlined  style={{  marginRight: '8px' }} />
        <span>{t.accountLogin}</span>
      </div>
      {/* <Title level={2} className="h5-form-title">输入密码</Title> */}
      <Text className="h5-form-description">
        {t.welcome}，{user}
      </Text>
      
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input.Password
          placeholder={t.passwordPlaceholder}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            // 用户输入时清除密码错误信息
            if (passwordError) {
              setPasswordError('');
            }
          }}
          size="large"
          status={passwordError ? 'error' : ''}
          style={{ 
            borderRadius: '8px',
            height: '44px',
            fontSize: '14px'
          }}
          onPressEnter={() => precheckHandlePassword({password})}
        />
        
        {passwordError && (
          <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
            {passwordError}
          </div>
        )}
        
        <Button
          size="large"
          type="primary"
          block
          style={{
            height: '44px',
            fontSize: '14px',
            borderRadius: '8px'
          }}
          onClick={() => precheckHandlePassword({password})}
          loading={verifyPwdLoading}
          disabled={!password}
        >
          {t.nextStep}
        </Button>

        {helpLink && (
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <a
              href={helpLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1890ff',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              {t.loginFailureTroubleshoot}
            </a>
          </div>
        )}
      </Space>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="h5-step-header"  onClick={handleBack} style={{ cursor: 'pointer'}}>
        <LeftCircleOutlined style={{ marginRight: '8px' }} />
        <span>{t.accountLogin}</span>
      </div>

      {contactError ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '24px' }}>
          <ExclamationCircleOutlined style={{ fontSize: '40px', color: '#ff4d4f', marginBottom: '16px' }} />
          <Text style={{ fontSize: '14px', color: '#595959', textAlign: 'center', marginBottom: '24px' }}>
            {t.contactError}
          </Text>
          <Button
            block
            onClick={handleBack}
          >
            {t.backButton}
          </Button>
        </div>
      ) : (
        <>
          <Title level={2} className="h5-form-title">{t.chooseVerification}</Title>
          <Text className="h5-form-description">
            {t.chooseVerificationDescription}
          </Text>
      
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {authMethods.includes(5) && contactInfo.canDisplayMobile && (
          <div 
            className="h5-verification-option" 
            onClick={contactInfo.mobile ? () => precheckHandleSendCode({type:'sms'}) : undefined} 
            style={{ 
              cursor: contactInfo.mobile ? 'pointer' : 'not-allowed',
              opacity: contactInfo.mobile ? 1 : 0.6
            }}
          >
            <div className="h5-verification-icon">
              <MobileOutlined />
            </div>
            <div className="h5-verification-content">
              {contactInfo.mobile ? (
                <>
                  <div className="h5-verification-primary">{contactInfo.mobile}</div>
                  <div className="h5-verification-secondary">{t.smsVerification}</div>
                </>
              ) : (
                <>
                  <div className="h5-verification-primary">{t.noMobileFound}</div>
                  <div className="h5-verification-secondary">{t.contactHR}</div>
                </>
              )}
            </div>
          </div>
        )}
        
        {authMethods.includes(4) && contactInfo.canDisplayEmail && (
          <div 
            className="h5-verification-option" 
            onClick={contactInfo.email ? () => precheckHandleSendCode({type:'email'}) : undefined} 
            style={{ 
              cursor: contactInfo.email ? 'pointer' : 'not-allowed',
              opacity: contactInfo.email ? 1 : 0.6
            }}
          >
            <div className="h5-verification-icon">
              <MailOutlined />
            </div>
            <div className="h5-verification-content">
              {contactInfo.email ? (
                <>
                  <div className="h5-verification-primary">{contactInfo.email}</div>
                  <div className="h5-verification-secondary">{t.emailVerification}</div>
                </>
              ) : (
                <>
                  <div className="h5-verification-primary">{t.noEmailFound}</div>
                  <div className="h5-verification-secondary">{t.contactHR}</div>
                </>
              )}
            </div>
          </div>
        )}
        
        {authMethods.includes(6) && (
          <div className="h5-verification-option" onClick={() => precheckHandleSendCode({type:'itoken'})} style={{ cursor: 'pointer' }}>
            <div className="h5-verification-icon">
              <SafetyOutlined />
            </div>
            <div className="h5-verification-content">
              <div className="h5-verification-primary">itoken</div>
              <div className="h5-verification-secondary">{t.itokenVerification}</div>
            </div>
          </div>
        )}
        
        {authMethods.includes(8) && contactInfo.canDisplayMobileForPhone && (
          <div 
            className="h5-verification-option" 
            onClick={contactInfo.mobile ? () => precheckHandleSendCode({type:'phone'}) : undefined} 
            style={{ 
              cursor: contactInfo.mobile ? 'pointer' : 'not-allowed',
              opacity: contactInfo.mobile ? 1 : 0.6
            }}
          >
            <div className="h5-verification-icon">
              <MobileOutlined />
            </div>
            <div className="h5-verification-content">
              {contactInfo.mobile ? (
                <>
                  <div className="h5-verification-primary">{contactInfo.mobile}</div>
                  <div className="h5-verification-secondary">{t.voiceVerification}</div>
                </>
              ) : (
                <>
                  <div className="h5-verification-primary">{t.noMobileFound}</div>
                  <div className="h5-verification-secondary">{t.contactHR}</div>
                </>
              )}
            </div>
          </div>
        )}
      </Space>
        </>
      )}
    </>
  );

  const renderStep4 = () => {
    const getTitle = () => {
      switch (selectedMethod) {
        case 'sms':
          return t.smsTitle;
        case 'email':
          return t.emailTitle;
        case 'itoken':
          return t.itokenTitle;
        case 'phone':
          return t.voiceTitle;
        default:
          return t.verificationTitle;
      }
    };

    const getDescription = () => {
      switch (selectedMethod) {
        case 'sms':
          return t.smsDescription(contactInfo.mobile);
        case 'email':
          return t.emailDescription(contactInfo.email);
        case 'itoken':
          return t.itokenDescription;
        case 'phone':
          return t.voiceDescription(contactInfo.mobile);
        default:
          return t.enterVerificationCode;
      }
    };

    return (
      <>
        <div className="h5-step-header" onClick={handleBack} style={{ cursor: 'pointer'}}>
          <LeftCircleOutlined style={{ marginRight: '8px' }} />
          <span>{t.accountLogin}</span>
        </div>
        <Title level={2} className="h5-form-title">{getTitle()}</Title>
        <Text className="h5-form-description">
          {getDescription()}
        </Text>
        
            <Input.OTP
              style={{ marginBottom: '15px'}}
              length={6}
              value={verificationCode}
              onChange={handleVerificationCodeChange}
              onPressEnter={handleVerificationSubmit}
              status={verificationCodeError ? 'error' : ''}
            />
            {verificationCodeError && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-11px', marginBottom: '15px' }}>
                <ExclamationCircleOutlined style={{ marginRight: '4px' }} />
                {verificationCodeError}
              </div>
            )}
          
          {/* 只有短信、邮箱和语音验证才显示倒计时 */}
          {(selectedMethod === 'sms' || selectedMethod === 'email' || selectedMethod === 'phone') && (
            <>
              <div className="h5-countdown-text">
                {countdown > 0 ? (
                  `${countdown}${t.countdownInvalid}`
                ) : (
                  <span>
                    {t.expired}
                    <Button type="link" onClick={() => precheckHandleResendCode({type: selectedMethod})} style={{padding: 0, height: 'auto'}}>
                      {t.resendCode}
                    </Button>
                  </span>
                )}
              </div>
              {countdown === 0 && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: '5px', lineHeight: '1.5' }}>
                  {t.switchVerificationMethodTip}
                </div>
              )}
            </>
          )}
          
          <Button
            size="large"
            type="primary"
            block
            style={{
              height: '44px',
              fontSize: '14px',
              borderRadius: '8px',
              marginBottom: '5px'
            }}
            loading={verifyMultiFactorLoading}
            onClick={handleVerificationSubmit}
            disabled={verificationCode.length !== 6}
          >
            {t.nextStep}
          </Button>
          
          <div className="h5-other-verification-link">
            <a onClick={handleBack}>
              {t.chooseOtherVerification}
            </a>
          </div>
      </>
    );
  };

  return (
    <div className="h5-container h5-login-page">
      <div className="h5-form-section">
        <div className="h5-form-content">
          <div className="h5-brand-header">
            <div className="h5-brand-logo">
              <img src={logo} alt="Logo" className="h5-login-logo" />
            </div>
            <LanguageSwitcher />
          </div>
          
          <div className="h5-form-container">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>
        </div>
      </div>
      <form ref={formRef} action={redirectUrl} method="post" style={{ display: 'none' }}>
        <input type="hidden" name="SAMLResponse" value={samlResponse || ''} />
        <input type="hidden" name="RelayState" value={relayState || ''} />
      </form>
      { showCaptcha && <Captcha onTokenReceived={handleTokenReceived} isMobile={true} /> }
    </div>
  );
};

export default H5LoginPage;
